// @flow

/**
 * This is a strange section managed by sales-dev which starts out invisible and collapsed.
 * Given a keyword value, it will inject a 3x1 pixel with the given keyword targeting onto the page.
 * If that pixel comes back empty, this section won't render anything,
 * and it will call a mixer service endpoint to notify the mixer's impression ETLs that it needs to update
 * the recorded positions of shelves below this
 * If the pixel does return data, that data is passed along to a consumer component, which
 * uses that data to render something, although it's likely to make additional service calls to hydrate itself first.
 *
 * @since Nov 2019
 * @author Mholland
 */

import React, { useState } from 'react';
import { useMutation } from 'urql';

import { ReportCollapsedFeedSection } from '@ta/social.feed';

import DmoCompShelfSponsor from './dmoSection/DmoCompShelfSponsor';

type Props = {
  /** The title value from OPF, getting from API call */
  keyword: string,
  locationId: number,
  clusterId: string,
  feedSectionId: string,
};

const DfpAdSection = ({ keyword, locationId, clusterId, feedSectionId }: Props) => {
  const [state, setState] = useState({
    shouldReportShelfCollapse: keyword.length === 0,
  });

  /**
   * If something goes wrong, ping the ReportCollapsedFeedSection mutation to alert the mixer ETL
   * to ignore this shelf during impression tracking.
   */
  const [, fireMutation] = useMutation(ReportCollapsedFeedSection);
  React.useEffect(() => {
    if (state.shouldReportShelfCollapse) {
      fireMutation({ feedSectionId }).catch(error => {
        const printId = feedSectionId === null ? 'Id not found' : feedSectionId;
        console.error(`Error occurred while recording collapsed feed section: ${printId}`, error);
      });
    }
  }, [state.shouldReportShelfCollapse, fireMutation, feedSectionId]);

  /**
   * Notification method used by children to kick off the useEffect hook above.
   */
  const reportCollapsed = () => {
    if (!state.shouldReportShelfCollapse) {
      setState({ shouldReportShelfCollapse: true });
    }
  };

  // if/when we decide to add various keywords, we'll need to do a conditional statement on the
  // keyword input, and only import the corresponding rendering component.
  return (
    <DmoCompShelfSponsor
      adPosition="feeddiscover"
      locationId={locationId}
      clusterId={clusterId}
      collapserFunction={reportCollapsed}
    />
  );
};

export default DfpAdSection;
